import React, { useMemo, useState } from 'react';

import { AddBusiness, ArrowDropDown, Search, Store } from '@mui/icons-material';
import {
  Avatar,
  Button,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { includes, isEmpty, map, partition } from 'lodash';
import { Link } from 'react-router-dom';
import type { ButtonProps } from '@mui/material';
import { AppContext } from '../../contexts/app_context';
import { createScopedI18n } from '../../i18n/i18n';
import useGetPermissions from '../use_get_permissions';
import type { RestaurantType } from '../../contexts/app_context';

const restaurantSelectorI18n = createScopedI18n('components.restaurant_selector');

export type RestaurantSelectorProps = ButtonProps & {
  group?: boolean;
};

export const RestaurantSelector = React.forwardRef(
  (
    { sx, group, ...props }: RestaurantSelectorProps,
    ref: ((instance: HTMLButtonElement | null) => void) | React.RefObject<HTMLButtonElement> | null | undefined,
  ) => {
    const {
      contextRestaurants,
      contextCurrentRestaurant,
      setContextCurrentRestaurant,
      setContextPermissions,
      contextUser,
    } = React.useContext(AppContext);

    const { getUserPermissions } = useGetPermissions();

    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
    const menuOpenHandler = (event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchor(event.currentTarget);
    };
    const menuCloseHandler = () => setMenuAnchor(null);

    const selectRestaurantHandlerHigherOrder = (restaurant: RestaurantType) => async () => {
      menuCloseHandler();

      setContextCurrentRestaurant(restaurant);
      if (contextUser?.chain?.id && restaurant?.id) {
        const newPermission = await getUserPermissions(contextUser.chain.id, restaurant.id);
        if (newPermission) {
          setContextPermissions((prev) => ({ ...prev, ...newPermission }));
        }
      }
    };

    const [filterInput, setFilterInput] = useState('');
    const filteredRestaurants = useMemo(() => {
      const _filteredRestaurants = contextRestaurants || [];
      if (filterInput === '') return _filteredRestaurants;
      return _filteredRestaurants.filter(
        (res) => includes(res.name, filterInput) || includes(res.branchName, filterInput),
      );
    }, [contextRestaurants, filterInput]);

    const [hiringRestaurants, notHiringRestaurants] = useMemo(() => {
      if (!filteredRestaurants) return [[], []]; // Handle case where filteredRestaurants is not ready
      return partition(filteredRestaurants, (i) => i.isHiring);
    }, [filteredRestaurants]);

    return (
      <>
        <Button
          ref={ref}
          color="inherit"
          size="small"
          onClick={menuOpenHandler}
          data-testid="restaurant-selector"
          sx={{ minHeight: 48, textTransform: 'none', textAlign: 'inherit', ...sx }}
          {...props}
        >
          <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
            <Avatar
              src={contextCurrentRestaurant?.frontPhotoUri}
              data-testid="restaurant-selector-avatar"
              sx={{ display: { xs: 'none', sm: 'inherit' }, marginRight: '4px' }}
            >
              <Store data-testid="restaurant-selector-avatar-fallback" />
            </Avatar>

            <Stack width={{ xs: 'calc(100% - 20px)', sm: 'calc(100% - 64px)' }} paddingLeft={{ sm: '4px' }}>
              {contextCurrentRestaurant ? (
                <>
                  <Typography noWrap display="inline-block" variant="caption" data-testid="restaurant-selector-name">
                    {contextCurrentRestaurant.name}
                  </Typography>
                  <Typography
                    noWrap
                    display="inline-block"
                    variant="subtitle2"
                    data-testid="restaurant-selector-branch-name"
                  >
                    {contextCurrentRestaurant.branchName}
                  </Typography>
                </>
              ) : (
                <Typography variant="subtitle2" data-testid="restaurant-selector-placeholder" sx={{ opacity: 0.6 }}>
                  {restaurantSelectorI18n('select_restaurant')}
                </Typography>
              )}
            </Stack>

            <Stack width="20px">
              <ArrowDropDown
                fontSize="small"
                sx={{ transform: menuAnchor ? 'rotate(180deg)' : 'rotate(360deg)', transition: '0.2s ease' }}
              />
            </Stack>
          </Stack>
        </Button>

        <Menu
          open={!!menuAnchor}
          anchorEl={menuAnchor}
          onClose={menuCloseHandler}
          slotProps={{ paper: { sx: { width: 330 } } }}
        >
          <MenuList disablePadding>
            <MenuItem>
              <TextField
                fullWidth
                autoFocus
                variant="standard"
                placeholder={restaurantSelectorI18n('filter_restaurant', { joinOutput: true })}
                value={filterInput}
                onKeyDown={(event) => event.stopPropagation()}
                onChange={(event) => setFilterInput(event.target.value)}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </MenuItem>
            {isEmpty(filteredRestaurants) ? (
              <MenuItem
                component={Link}
                to="/restaurants/new"
                divider
                onClick={menuCloseHandler}
                sx={{ gap: 1, height: 48 }}
              >
                <ListItemIcon sx={{ width: 40, justifyContent: 'center' }}>
                  <AddBusiness />
                </ListItemIcon>
                {restaurantSelectorI18n('add_restaurant')}
              </MenuItem>
            ) : group ? (
              <>
                <div data-testid="restaurant-selector-hiring-group" style={{ marginBottom: '34px' }}>
                  <p style={{ paddingLeft: '16px', marginBottom: '5px' }}>กำลังประกาศจ้าง</p>
                  {map(hiringRestaurants, (restaurant) => (
                    <MenuItem
                      key={restaurant.id}
                      divider
                      onClick={selectRestaurantHandlerHigherOrder(restaurant)}
                      data-testid={`restaurant-selector-item-${restaurant.id}`}
                    >
                      <Avatar src={restaurant.frontPhotoUri} sx={{ marginRight: '4px' }}>
                        <Store />
                      </Avatar>

                      <Stack width="calc(100% - 44px)" paddingLeft="4px">
                        <Typography noWrap display="inline-block" variant="caption">
                          {restaurant.name}
                        </Typography>
                        <Typography noWrap display="inline-block" variant="subtitle2">
                          {restaurant.branchName}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </div>

                <div data-testid="restaurant-selector-non-hiring-group" style={{ marginBottom: '15px' }}>
                  <p style={{ paddingLeft: '16px', marginBottom: '5px' }}>ไม่มีประกาศจ้าง</p>
                  {map(notHiringRestaurants, (restaurant) => (
                    <MenuItem
                      key={restaurant.id}
                      divider
                      onClick={selectRestaurantHandlerHigherOrder(restaurant)}
                      data-testid={`restaurant-selector-item-${restaurant.id}`}
                    >
                      <Avatar src={restaurant.frontPhotoUri} sx={{ marginRight: '4px' }}>
                        <Store />
                      </Avatar>

                      <Stack width="calc(100% - 44px)" paddingLeft="4px">
                        <Typography noWrap display="inline-block" variant="caption">
                          {restaurant.name}
                        </Typography>
                        <Typography noWrap display="inline-block" variant="subtitle2">
                          {restaurant.branchName}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </div>
              </>
            ) : (
              <div>
                {map(filteredRestaurants, (restaurant) => (
                  <MenuItem
                    key={restaurant.id}
                    divider
                    onClick={selectRestaurantHandlerHigherOrder(restaurant)}
                    data-testid={`restaurant-selector-item-${restaurant.id}`}
                  >
                    <Avatar src={restaurant.frontPhotoUri} sx={{ marginRight: '4px' }}>
                      <Store />
                    </Avatar>

                    <Stack width="calc(100% - 44px)" paddingLeft="4px">
                      <Typography noWrap display="inline-block" variant="caption">
                        {restaurant.name}
                      </Typography>
                      <Typography noWrap display="inline-block" variant="subtitle2">
                        {restaurant.branchName}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </div>
            )}
          </MenuList>
        </Menu>
      </>
    );
  },
);
